<script setup lang="ts">
/** Picks up to ten contacts of the workspace to send as WhatsApp contact cards. */
import { computed, ref, watch } from "vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import Avatar from "./Avatar.vue";
import SearchField from "./SearchField.vue";
import { contactsListApi, type ContactListRow } from "../api/organization";
import { t } from "../i18n";
import { profileTone } from "../composables/profileColors";

const MAX = 10;
const props = defineProps<{ open: boolean; slug: string; busy?: boolean }>();
const emit = defineEmits<{ close: []; send: [ids: string[]] }>();

const query = ref("");
const rows = ref<ContactListRow[]>([]);
const chosen = ref<string[]>([]);
const loading = ref(false);
let timer: ReturnType<typeof setTimeout> | undefined;

async function load() {
  loading.value = true;
  try {
    const res = await contactsListApi.list(props.slug, {
      ...(query.value.trim() ? { q: query.value.trim() } : {}),
      limit: 30,
    });
    rows.value = res.contacts.filter((c) => c.address);
  } finally {
    loading.value = false;
  }
}
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    query.value = "";
    chosen.value = [];
    void load();
  },
  { immediate: true },
);
watch(query, () => {
  clearTimeout(timer);
  timer = setTimeout(load, 250);
});

const full = computed(() => chosen.value.length >= MAX);
function toggle(id: string) {
  const i = chosen.value.indexOf(id);
  if (i >= 0) chosen.value.splice(i, 1);
  else if (!full.value) chosen.value.push(id);
}
</script>

<template>
  <Modal :open="open" :title="t('share_contact.title')" @close="emit('close')">
    <div class="stack contact-share">
      <SearchField v-model="query" :placeholder="t('share_contact.search')" />
      <p v-if="!rows.length && !loading" class="muted small">
        {{ t("share_contact.empty") }}
      </p>
      <div class="contact-share-list scroll">
        <label
          v-for="c in rows"
          :key="c.id"
          class="list-row contact-share-row"
          :class="{ disabled: full && !chosen.includes(c.id) }"
        >
          <input
            type="checkbox"
            class="wa-check"
            :checked="chosen.includes(c.id)"
            :disabled="full && !chosen.includes(c.id)"
            @change="toggle(c.id)"
          />
          <Avatar
            :name="c.displayName"
            :tone="profileTone(c.address, c.id)"
            :size="36"
          />
          <span class="grow">
            <strong>{{ c.displayName }}</strong>
            <small class="muted">{{ c.address }}</small>
          </span>
        </label>
      </div>
    </div>
    <template #footer>
      <span class="grow small muted">{{
        t("share_contact.count", { count: chosen.length, max: MAX })
      }}</span>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        icon="send"
        :disabled="!chosen.length"
        :loading="busy"
        @click="emit('send', chosen.slice())"
        >{{ t("share_contact.send") }}</Button
      >
    </template>
  </Modal>
</template>
